import React, { useState, useEffect } from "react";
import {
    Box,
    Alert,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Typography,
} from "@mui/material";
import {
    InsertDriveFile as FileIcon,
    VerifiedUser as PermitIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useGetDocumentsByEntityWithAuthQuery, DocumentResponseDto } from "../../api/documentsApi";
import PasswordModal from "../../components/common/PasswordModal/PasswordModal";
import { DataTable } from "../../components/common/DataTable/DataTable";
import {
    createDocumentActions,
    createDocumentColumns,
    getDocumentRowSx,
    isDocumentExpired,
    isDocumentExpiringSoon,
} from "../../components/documents/documentColumns";

const VehiclePermitsPage: React.FC = () => {
    const { vehicleId } = useParams<{ vehicleId: string }>();
    const vehicleIdNumber = parseInt(vehicleId || "0", 10);

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
    const [ssn, setSsn] = useState<string>("");

    const {
        data: documents,
        isLoading,
        error,
    } = useGetDocumentsByEntityWithAuthQuery({
        entityType: "Vehicle",
        entityId: vehicleIdNumber,
        ssn: ssn,
        includeInactive: false, // Only show active documents
    }, {
        skip: !isAuthenticated || !ssn, // Only fetch documents if authenticated and SSN is provided
    });

    // Filter only permit documents
    const permitDocuments = documents?.filter(doc => doc.isPermit && doc.isActive) || [];

    // Show password modal when component loads
    useEffect(() => {
        if (vehicleIdNumber && !isAuthenticated) {
            setPasswordModalOpen(true);
        }
    }, [vehicleIdNumber, isAuthenticated]);

    const handlePasswordSubmit = async (password: string) => {
        setIsVerifyingPassword(true);
        setPasswordError(null);

        try {
            // Set the SSN and trigger the API call
            setSsn(password);
            setIsAuthenticated(true);
            setPasswordModalOpen(false);
            setPasswordError(null);
        } catch (error) {
            setPasswordError("An error occurred while verifying the SSN. Please try again.");
        } finally {
            setIsVerifyingPassword(false);
        }
    };

    const handlePasswordModalClose = () => {
        if (!isAuthenticated) {
            // If not authenticated, redirect back or show error
            window.history.back();
        }
        setPasswordModalOpen(false);
        setPasswordError(null);
    };

    const handleDownload = (document: DocumentResponseDto) => {
        window.open(document.url, '_blank');
    };

    const handleView = (document: DocumentResponseDto) => {
        window.open(document.url, '_blank');
    };

    const documentColumns = createDocumentColumns({
        includeStatusColumn: false,
        includePermitColumn: false,
    });
    const documentActions = createDocumentActions({
        onDownload: handleDownload,
        onView: handleView,
    });

    // Show loading while getting documents (only if authenticated)
    if (isAuthenticated && isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "400px",
                    }}
                >
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    // Show error if documents cannot be loaded (only if authenticated)
    if (isAuthenticated && error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">
                    <Typography variant="h6" gutterBottom>
                        Access Denied
                    </Typography>
                    <Typography variant="body2">
                        Invalid SSN. Please check the vehicle's SSN number and try again.
                    </Typography>
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <PermitIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                    Vehicle Permits
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    Vehicle ID: {vehicleId}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Official permit documents for this vehicle
                </Typography>
            </Box>

            {permitDocuments.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: "center", py: 6 }}>
                        <FileIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Permit Documents Available
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            There are currently no active permit documents for this vehicle.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Summary */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Document Summary
                            </Typography>
                            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                <Typography variant="body2">
                                    <strong>Total Permits:</strong> {permitDocuments.length}
                                </Typography>
                                <Typography variant="body2" color="error">
                                    <strong>Expired:</strong> {permitDocuments.filter(d => isDocumentExpired(d.expirationDate)).length}
                                </Typography>
                                <Typography variant="body2" color="warning.dark">
                                    <strong>Expiring Soon:</strong> {permitDocuments.filter(d => isDocumentExpiringSoon(d.expirationDate) && !isDocumentExpired(d.expirationDate)).length}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    <DataTable<DocumentResponseDto>
                        items={permitDocuments}
                        columns={documentColumns}
                        actions={documentActions}
                        totalCount={permitDocuments.length}
                        getRowSx={getDocumentRowSx}
                    />

                    {/* Footer Info */}
                    <Box sx={{ mt: 3, textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary">
                            This page displays only active permit documents for verification purposes.
                        </Typography>
                    </Box>
                </>
            )}

            {/* Password Modal */}
            <PasswordModal
                open={passwordModalOpen}
                onClose={handlePasswordModalClose}
                onPasswordSubmit={handlePasswordSubmit}
                title="Vehicle Permit Access"
                description="Enter the vehicle's SSN to access permit documents"
                isLoading={isVerifyingPassword}
                error={passwordError}
            />
        </Container>
    );
};

export default VehiclePermitsPage;

